import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Empty, Input, Popover, Segmented, Select, Spin } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import useAxiosClient from '../../axios-client';
import { STATUS } from '../../shellTokens';
import '../Settings/RoleSettings/access.css';

/**
 * One user's roles and effective access.
 *
 * The screen answers "what can this person do, and why". Every permission they
 * hold carries its source, and the source decides what can be done about it:
 * a role-derived permission links to the role editor rather than pretending it
 * can be removed here, because removing it would change every other holder of
 * that role. Only direct grants are revocable from this page.
 *
 * Direct grants add to a role and never subtract — there are no denials — so
 * the header counts them as a number in its own right: a user accumulating
 * direct grants is a role nobody got round to creating.
 */

const ROLE_LINK = '/settings/roleSettings';
const EDIT_MIN_WIDTH = 768;

const useReadOnlyViewport = () => {
  const [readOnly, setReadOnly] = useState(() => window.innerWidth < EDIT_MIN_WIDTH);
  useEffect(() => {
    const onResize = () => setReadOnly(window.innerWidth < EDIT_MIN_WIDTH);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return readOnly;
};

const matchOf = (row, term) => {
  if (!term) return true;
  const t = term.toLowerCase();
  return row.name.toLowerCase().includes(t)
    || (row.description || '').toLowerCase().includes(t)
    || (row.group || '').toLowerCase().includes(t);
};

/**
 * Source chips: roles alphabetically, capped at two, with the direct grant
 * always last so the removable thing is always in the same place.
 */
const SourceChips = ({ row, onRevoke, readOnly }) => {
  if (!row.held) {
    return <span style={{ font: '400 11.5px Outfit, sans-serif', color: 'var(--dim)' }}>not held</span>;
  }

  // Two chips wide at most. A direct grant takes one of those slots, so it
  // leaves room for a single role name beside it.
  const maxNames = row.direct ? 1 : 2;
  const collapsed = row.via.length > maxNames;

  return (
    <span style={{ display: 'flex', alignItems: 'center', gap: 5, flexWrap: 'nowrap' }}>
      {!collapsed && row.via.map((role) => (
        <Link key={role} to={ROLE_LINK} className="et-access__chip et-access__chip--via" title={`Granted by the ${role} role`}>
          {role}
        </Link>
      ))}
      {collapsed && (
        <Popover
          title="Granted by"
          content={<div style={{ display: 'grid', gap: 4 }}>{row.via.map((r) => <span key={r}>{r}</span>)}</div>}
        >
          {/* One chip, not two: the count is the information, the other role
              names are not — the popover has them. */}
          <Link to={ROLE_LINK} className="et-access__chip et-access__chip--via">
            {`${row.via[0]} +${row.via.length - 1}`}
          </Link>
        </Popover>
      )}
      {row.direct && (
        <span className="et-access__chip et-access__chip--direct">
          DIRECT
          {!readOnly && (
            <button
              type="button"
              onClick={onRevoke}
              title={`Revoke the direct ${row.name} grant`}
              aria-label={`Revoke the direct ${row.name} grant`}
            >
              ✕
            </button>
          )}
        </span>
      )}
    </span>
  );
};

const UserAccess = ({ userId, userName, canManageRoles, canManageGrants }) => {
  const axiosClient = useAxiosClient();
  const readOnlyViewport = useReadOnlyViewport();

  const [access, setAccess] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [allRoles, setAllRoles] = useState([]);
  const [addingRole, setAddingRole] = useState(false);
  const [search, setSearch] = useState('');
  const [scope, setScope] = useState('all');
  const searchRef = useRef(null);

  const readOnly = readOnlyViewport || (!canManageRoles && !canManageGrants);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axiosClient.get(`/users/${userId}/access`);
      setAccess(response.data);
    } catch (error) {
      const message = (error.response && error.response.data && error.response.data.message)
        || error.message || error.toString();
      toast.error(message);
    } finally {
      setLoading(false);
    }
  }, [axiosClient, userId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!canManageRoles) return;
    axiosClient.get('/settings/getRoles')
      .then((r) => setAllRoles(r.data))
      .catch(() => { /* the picker just stays empty */ });
  }, [axiosClient, canManageRoles]);

  /** Every mutation returns the fresh access payload, so no refetch is needed. */
  const apply = async (request, successMessage) => {
    setBusy(true);
    try {
      const response = await request();
      if (response.data.access) setAccess(response.data.access);
      toast.success(successMessage ?? response.data.message);
    } catch (error) {
      const message = (error.response && error.response.data && error.response.data.message)
        || error.message || error.toString();
      toast.error(message);
    } finally {
      setBusy(false);
    }
  };

  const setRoles = (roles) => apply(
    () => axiosClient.put(`/users/${userId}/roles`, { roles }),
  );

  const grant = (permission) => apply(
    () => axiosClient.post(`/users/${userId}/permissions`, { permission }),
  );

  const revoke = (permission) => apply(
    () => axiosClient.delete(`/users/${userId}/permissions/${encodeURIComponent(permission)}`),
  );

  const groups = useMemo(() => {
    if (!access) return [];
    const term = search.trim();
    const byGroup = new Map();

    access.permissions.forEach((row) => {
      if (scope === 'held' && !row.held) return;
      if (!matchOf(row, term)) return;
      if (!byGroup.has(row.group)) byGroup.set(row.group, []);
      byGroup.get(row.group).push(row);
    });

    return [...byGroup.entries()].map(([name, rows]) => ({
      name,
      rows,
      held: rows.filter((r) => r.held).length,
      // Denominator is the group's real size, not the filtered slice.
      total: access.permissions.filter((p) => p.group === name).length,
    }));
  }, [access, search, scope]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== '/' || e.metaKey || e.ctrlKey || e.altKey) return;
      const tag = document.activeElement?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA') return;
      e.preventDefault();
      searchRef.current?.focus();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  if (loading) return <div style={{ padding: 48, textAlign: 'center' }}><Spin /></div>;
  if (!access) return null;

  const { summary, roles } = access;
  const overGranted = summary.direct > summary.directSoftLimit;
  const firstName = (userName || 'This user').split(' ')[0];

  return (
    <div className="et-access" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {readOnlyViewport && (
        <div className="et-access__notice">
          Read-only on this screen size. Open Access on a desktop to change roles or grants.
        </div>
      )}

      {/* ---- Roles ---- */}
      <section className="et-access__card">
        <div className="et-access__cardhead">
          <div>
            <div style={{ font: '700 14.5px Outfit, sans-serif' }}>Roles</div>
            <div style={{ font: '400 12px Outfit, sans-serif', color: 'var(--muted)', marginTop: 2 }}>
              Everything a role grants applies to this user everywhere.
            </div>
          </div>
        </div>
        <div style={{ padding: 16, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          {roles.map((role) => (
            <span key={role} className="et-access__chip et-access__chip--role">
              <Link to={ROLE_LINK}>{role}</Link>
              {!readOnly && canManageRoles && (
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => setRoles(roles.filter((r) => r !== role))}
                  title={`Remove ${role}`}
                  aria-label={`Remove the ${role} role`}
                >
                  ✕
                </button>
              )}
            </span>
          ))}

          {roles.length === 0 && (
            <span style={{ font: '400 12.5px Outfit, sans-serif', color: 'var(--dim)' }}>
              No roles.
            </span>
          )}

          {!readOnly && canManageRoles && (
            addingRole ? (
              <Select
                autoFocus
                showSearch
                open
                placeholder="Add a role"
                style={{ minWidth: 220 }}
                onBlur={() => setAddingRole(false)}
                onSelect={(value) => { setAddingRole(false); setRoles([...roles, value]); }}
                options={allRoles
                  .filter((r) => !roles.includes(r.name))
                  .map((r) => ({ value: r.name, label: `${r.name} — ${r.permissionsCount} permissions` }))}
              />
            ) : (
              <Button size="small" disabled={busy} onClick={() => setAddingRole(true)}>+ Add role</Button>
            )
          )}
        </div>

        {roles.length === 0 && (
          <div className="et-access__emptyroles">
            <div style={{ font: '700 13.5px Outfit, sans-serif', marginBottom: 4 }}>No roles, no access</div>
            <div style={{ font: '400 12.5px/1.6 Outfit, sans-serif', color: 'var(--muted)' }}>
              {firstName} can sign in and see nothing but their own profile. Assign a role to give
              them a job; grant a permission directly only if no role fits — that is usually a sign
              the role is missing.
            </div>
          </div>
        )}
      </section>

      {/* ---- Effective access ---- */}
      <section className="et-access__card">
        <div className="et-access__cardhead">
          <div>
            <div style={{
              font: '600 11.5px Outfit, sans-serif',
              color: 'var(--muted)',
              letterSpacing: '.04em',
              textTransform: 'uppercase',
            }}>
              Effective access
            </div>
            <div style={{ font: '400 13px Outfit, sans-serif', color: 'var(--muted)', marginTop: 4 }}>
              <span className="et-access__meta" style={{ fontSize: 15, color: 'var(--text)', fontWeight: 700 }}>
                {`${summary.held} of ${summary.total}`}
              </span>
              {' permissions · '}
              <span
                className="et-access__meta"
                style={{ color: overGranted ? STATUS.warn : 'var(--muted)', fontWeight: 600 }}
              >
                {summary.direct}
              </span>
              {' granted directly'}
              {overGranted && (
                <span style={{ color: STATUS.warn }}>{' — consider a role'}</span>
              )}
              {` · ${summary.roles} role${summary.roles === 1 ? '' : 's'}`}
            </div>
          </div>
        </div>

        <div style={{
          padding: '12px 18px',
          borderBottom: '1px solid var(--border)',
          display: 'flex',
          gap: 10,
          alignItems: 'center',
          flexWrap: 'wrap',
        }}>
          <Input
            ref={searchRef}
            allowClear
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search permissions"
            prefix={<SearchOutlined style={{ color: 'var(--dim)' }} />}
            style={{ flex: 1, minWidth: 200 }}
          />
          <Segmented
            value={scope}
            onChange={setScope}
            options={[
              { label: `All ${summary.total}`, value: 'all' },
              { label: 'Held only', value: 'held' },
            ]}
          />
        </div>

        <div style={{ padding: '10px 18px 18px', display: 'flex', flexDirection: 'column', gap: 10 }}>
          {groups.length === 0 && (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={search.trim() ? `Nothing matches "${search.trim()}".` : 'Nothing held yet.'}
              style={{ padding: '28px 0' }}
            />
          )}

          {groups.map((group) => (
            <div key={group.name} className="et-access__group">
              <div className="et-access__grouphead" style={{ cursor: 'default' }}>
                <span style={{ font: '700 13px Outfit, sans-serif' }}>{group.name}</span>
                <span className="et-access__meta" style={{ marginLeft: 'auto' }}>
                  {`${group.held} of ${group.total}`}
                </span>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {group.rows.map((row) => (
                  <div
                    key={row.name}
                    className={`et-access__row et-access__row--user${row.held ? '' : ' et-access__row--unheld'}`}
                  >
                    <span className="et-access__rowtext">
                      <span className={`et-access__name${row.held ? '' : ' et-access__name--off'}`}>
                        {row.name}
                      </span>
                      <span className={`et-access__desc${row.held ? '' : ' et-access__desc--off'}`}>
                        {row.description}
                      </span>
                    </span>

                    <span style={{ display: 'flex', alignItems: 'center', gap: 8, justifySelf: 'end' }}>
                      <SourceChips
                        row={row}
                        readOnly={readOnly || !canManageGrants}
                        onRevoke={() => revoke(row.name)}
                      />
                      {!row.held && !readOnly && canManageGrants && (
                        <button
                          type="button"
                          className="et-access__grantbtn"
                          disabled={busy}
                          onClick={() => grant(row.name)}
                        >
                          + Grant directly
                        </button>
                      )}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div style={{
          padding: '12px 18px',
          borderTop: '1px solid var(--border)',
          font: '400 11.5px/1.6 Outfit, sans-serif',
          color: 'var(--dim)',
        }}>
          Role-derived rows cannot be revoked here — open the role to change what it grants.
          Direct grants add to a role and never subtract.
        </div>
      </section>
    </div>
  );
};

export default UserAccess;
